// formContent is sent as formContent[key]=<stringified-value> so PHP receives an associative array.

import type {ProjectData} from 'grapesjs';
import type {FieldMeta} from '@FormMakerReact/form-maker.types';
import {CSRF_TOKEN_HEADER, getCsrfToken} from '@Utils/csrf.util';

const EXISTING_SAVE_DATA_ERROR_CODE = 'existing-save-data';

export enum SaveActionType {
  ATTEMPT = 'ATTEMPT',
  FORCE = 'FORCE',
}

export enum SaveType {
  SOFT = 'soft',
  HARD = 'hard',
}

export interface SaveFormArgs {
  idEmailSignupForm: string;
  name: string;
  organizationName: string;
  formContent: ProjectData;
  htmlContent: string;
  fieldsMeta: readonly FieldMeta[];
  saveActionType: SaveActionType;
  saveType: SaveType;
}

interface RawLoadFormResponse {
  formContent: string;
  name: string;
  fieldsMeta: unknown;
}

interface JsonEnvelope<T> {
  status?: string;
  data?: T;
  error?: string;
  code?: string;
  message?: string;
}

export interface LoadFormResponse {
  formContent: ProjectData;
  name: string;
  fieldsMeta: unknown;
}

export interface ConcurrentEditPayload {
  currentUserPictureURL: string;
  otherUserPictureURL: string;
  otherUserName: string;
}

export class ConcurrentEditError extends Error {
  constructor(public readonly payload: ConcurrentEditPayload) {
    super('Form was modified by someone else.');
    this.name = 'ConcurrentEditError';
  }
}

/** Wire values emitted by Formmaker::asyncSaveEmailSignupForm via loadJSONErrorResponse's `code` field. Must match SignupFormErrorCode (PHP) byte-for-byte. */
export enum SignupFormSaveErrorCode {
  AUTHENTICATE_USER = 'authenticate-user',
  LIMIT_EXCEEDED = 'signup-form-limit-exceeded',
}

export class SaveFormRejectedError extends Error {
  constructor(displayMessage: string) {
    super(displayMessage);
    this.name = 'SaveFormRejectedError';
  }
}

/** The server would not create the form: the owner is at MAX_SIGNUP_FORMS. Carries the server's user-facing message. */
export class SignupFormLimitExceededError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SignupFormLimitExceededError';
  }
}

export class SignupFormGateError extends Error {
  constructor(public readonly code: SignupFormSaveErrorCode) {
    super(`saveForm rejected by gate: ${code}`);
    this.name = 'SignupFormGateError';
  }
}

/** Thrown by the storage adapter when the client-side gate (ensureCanSaveSignupForm) blocks a store before any network attempt. */
export class SignupFormGateBlockedError extends Error {
  constructor() {
    super('store blocked by client-side gate');
    this.name = 'SignupFormGateBlockedError';
  }
}

/** True for a rejection the save gate produced — client-side block or server round-trip refusal — as opposed to a genuine save failure. */
export function isSignupFormGateRejection(err: unknown): err is SignupFormGateError | SignupFormGateBlockedError {
  return err instanceof SignupFormGateError || err instanceof SignupFormGateBlockedError;
}

async function postFormData(url: string, params: Record<string, string>): Promise<Response> {
  const formData = new FormData();
  Object.entries(params).forEach(([k, v]) => {
    formData.append(k, v);
  });
  return postWithCsrfToken(url, formData);
}

async function postWithCsrfToken(url: string, body: FormData): Promise<Response> {
  return fetch(url, {method: 'POST', body, credentials: 'same-origin', headers: {[CSRF_TOKEN_HEADER]: await getCsrfToken()}});
}

// Nested keys so $_POST['fieldsMeta'] arrives as an associative array of arrays for FieldsMeta::fromJSON.
function appendSerializedFieldsMeta(formData: FormData, fieldsMeta: readonly FieldMeta[]): void {
  fieldsMeta.forEach((meta, index) => {
    formData.append(`fieldsMeta[${index.toString()}][component]`, meta.component);
    formData.append(`fieldsMeta[${index.toString()}][order]`, String(meta.order));
    formData.append(`fieldsMeta[${index.toString()}][label]`, meta.label);
    formData.append(`fieldsMeta[${index.toString()}][format]`, meta.format);
    if (meta.customFieldIndex !== undefined) {
      formData.append(`fieldsMeta[${index.toString()}][customFieldIndex]`, String(meta.customFieldIndex));
    }
    if (meta.idUserHasCustomField !== undefined) {
      formData.append(`fieldsMeta[${index.toString()}][idUserHasCustomField]`, String(meta.idUserHasCustomField));
    }
  });
}

export async function loadForm(idEmailSignupForm: string): Promise<LoadFormResponse> {
  const url = window.PMW.util.site_url('formmaker/asyncLoadEmailSignupForm');
  const res = await postFormData(url, {idEmailSignupForm});
  if (!res.ok) {
    throw new Error(`loadForm failed: ${res.status.toString()}`);
  }
  const envelope = (await res.json()) as JsonEnvelope<RawLoadFormResponse>;
  if (envelope.status !== 'success' || !envelope.data) {
    throw new Error(`loadForm rejected: ${envelope.error ?? 'unknown error'}`);
  }
  return {
    formContent: parseFormContent(envelope.data.formContent),
    name: envelope.data.name,
    fieldsMeta: envelope.data.fieldsMeta,
  };
}

// Each per-section value is a JSON string; parse the outer envelope then JSON.parse each inner value.
function parseFormContent(raw: string): ProjectData {
  if (!raw) {
    return {};
  }
  const outer = JSON.parse(raw) as Record<string, unknown>;
  const parsed: Record<string, unknown> = {};
  Object.entries(outer).forEach(([key, value]) => {
    parsed[key] = typeof value === 'string' ? JSON.parse(value) : value;
  });
  return parsed;
}

// Per-key stringify + nested FormData keys so PHP receives formContent as an array, not a scalar.
function appendSerializedFormContent(formData: FormData, data: ProjectData): void {
  Object.entries(data).forEach(([key, value]) => {
    formData.append(`formContent[${key}]`, JSON.stringify(value));
  });
}

export interface SaveFormResponse {
  idEmailSignupForm: string;
  fieldsMeta: FieldMeta[];
}

export async function saveForm(args: SaveFormArgs): Promise<SaveFormResponse> {
  const url = window.PMW.util.site_url('formmaker/asyncSaveEmailSignupForm');
  const formData = new FormData();
  formData.append('idEmailSignupForm', args.idEmailSignupForm);
  formData.append('name', args.name);
  formData.append('organizationName', args.organizationName);
  appendSerializedFormContent(formData, args.formContent);
  formData.append('htmlContent', args.htmlContent);
  formData.append('saveActionType', args.saveActionType);
  formData.append('saveType', args.saveType);
  appendSerializedFieldsMeta(formData, args.fieldsMeta);
  const res = await postWithCsrfToken(url, formData);
  if (!res.ok) {
    throw new Error(`saveForm failed: ${res.status.toString()}`);
  }
  const envelope = (await res.json()) as JsonEnvelope<{
    idEmailSignupForm?: string;
    fieldsMeta?: unknown;
    error?: string;
    currentUserPictureURL?: string;
    otherUserPictureURL?: string;
    otherUserName?: string;
  }>;
  if (envelope.code === EXISTING_SAVE_DATA_ERROR_CODE || envelope.error === EXISTING_SAVE_DATA_ERROR_CODE || envelope.data?.error === EXISTING_SAVE_DATA_ERROR_CODE) {
    throw new ConcurrentEditError({
      currentUserPictureURL: envelope.data?.currentUserPictureURL ?? '',
      otherUserPictureURL: envelope.data?.otherUserPictureURL ?? '',
      otherUserName: envelope.data?.otherUserName ?? '',
    });
  }
  const gateCode = envelope.code;
  if (gateCode === SignupFormSaveErrorCode.AUTHENTICATE_USER) {
    throw new SignupFormGateError(gateCode);
  }
  if (gateCode === SignupFormSaveErrorCode.LIMIT_EXCEEDED) {
    throw new SignupFormLimitExceededError(envelope.message ?? '');
  }
  if (envelope.status !== 'success' && envelope.message) {
    throw new SaveFormRejectedError(envelope.message);
  }
  if (envelope.status !== 'success' || !envelope.data?.idEmailSignupForm) {
    throw new Error('saveForm response missing idEmailSignupForm');
  }
  return {
    idEmailSignupForm: envelope.data.idEmailSignupForm,
    fieldsMeta: parseResponseFieldsMeta(envelope.data.fieldsMeta),
  };
}

function parseResponseFieldsMeta(raw: unknown): FieldMeta[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((entry): entry is FieldMeta => {
    if (typeof entry !== 'object' || entry === null) {
      return false;
    }
    const e = entry as Record<string, unknown>;
    return typeof e.component === 'string' && typeof e.order === 'number' && typeof e.label === 'string' && typeof e.format === 'string';
  });
}
